import React from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes as ReactRoutes } from 'react-router-dom';
import AddressBook from 'src/app/AddressBook';
import Dashboard from 'src/app/Dashboard';
import DocumentHub from 'src/app/DocumentHub';
import Integration from 'src/app/Integration';
import ForgotPassword from 'src/app/Login/ForgotPassword';
import MyQuote from 'src/app/MyQuote';
import AddQuote from 'src/app/MyQuote/AddQuote/NewAddQuote';
import BookingStepper from 'src/app/MyQuote/BookingStepper';
import CreateQuote from 'src/app/MyQuote/CreateQuote/NewCreateQuote';
import QuoteDetail from 'src/app/MyQuote/QuoteDetail';
import Orders from 'src/app/Orders';
import OrderView from 'src/app/Orders/OrderView';
import Organization from 'src/app/Organization';
import PublicTracking from 'src/app/PublicTracking';
import SetPassword from 'src/app/SetPassword';
import CreatePassword from 'src/app/SetPassword/CreatePassword';
import AccountSettings from 'src/app/Settings';
import SharedQuote from 'src/app/SharedQuote';
import ShareOrder from 'src/app/ShareOrder';
import ShareRadarOrder from 'src/app/ShareRadarOrder';
import Track from 'src/app/Track';
import TrackOrderDetail from 'src/app/Track/RadarOrderDetail';
import TrackMapLayout from 'src/app/Track/TrackMapLayout';
import Verification from 'src/app/Verification';

import AppLayout from '../app/AppLayout';
import Callback from '../app/Callback';
import PageNotFound from '../app/Errors/404';
import Login from '../app/Login';
import MicrosoftCallback from '../app/MicrosoftCallback';
import SetupAccount from '../app/SetupAccount';
import AuthCheck from '../components/AuthCheck';
import { PERMISSION } from '../constants/common';
import { isPublicTrackingHost, ROUTES } from '../constants/routes';

import OnboardingRoute from './OnboardingRoute';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';

const Routes = () => {
    const { t } = useTranslation();

    // track.westernalliancelogistics.com: only the public search, at /; every other path goes back to
    // /. Rendered without AuthCheck on purpose: nothing on this hostname depends on a portal session.
    //
    // /public-tracking is served here too, even though this host hands out the bare / URL. The portal
    // hostname reaches the same page at that path, so the two can be confused, and a link that
    // already went out carrying it must still open. Without this it hit the catch-all below.
    //
    // The catch-all keeps the query string. It used to drop it, so /public-tracking?walId=WAL-… on
    // this host redirected to a bare / and the shipment id was gone — a search for nothing, with no
    // sign anything had been lost.
    if (isPublicTrackingHost()) {
        return (
            <BrowserRouter>
                <ReactRoutes>
                    <Route path={ROUTES.HOME} element={<PublicTracking />} />
                    <Route path={ROUTES.PUBLIC_TRACKING} element={<PublicTracking />} />
                    <Route path="*" element={<Navigate to={`${ROUTES.HOME}${window.location.search}`} replace />} />
                </ReactRoutes>
            </BrowserRouter>
        );
    }

    return (
        <BrowserRouter>
            <AuthCheck>
                <ReactRoutes>
                    <Route path={ROUTES.HOME} element={<AppLayout />}>
                        <Route path={ROUTES.LOGIN} element={<PublicRoute component={Login} title={t('RoutesTitle.Login')} />} />

                        <Route path={ROUTES.SET_PASSWORD} element={<PublicRoute component={SetPassword} title={t('RoutesTitle.SetPassword')} />} />
                        <Route path={ROUTES.ACCEPT_INVITE} element={<PublicRoute component={Verification} title={t('RoutesTitle.Verification')} />} />

                        <Route path={ROUTES.FORGOT_PASSWORD} element={<PublicRoute component={ForgotPassword} title={t('RoutesTitle.ForgotPassword')} />} />

                        <Route path={ROUTES.SETUP_ACCOUNT} element={<OnboardingRoute component={SetupAccount} title={t('RoutesTitle.SetupAccount')} />} />

                        <Route path={ROUTES.CREATE_PASSWORD} element={<PublicRoute component={CreatePassword} title={t('RoutesTitle.CreatePassword')} />} />
                        <Route index path={ROUTES.HOME} element={<PrivateRoute component={Dashboard} title={t('RoutesTitle.Dashboard')} />} />
                        <Route index path={ROUTES.ORGANIZATION} element={<PrivateRoute component={Organization} title={t('RoutesTitle.Organization')} />} />
                        <Route index path={ROUTES.DOCUMENTATION_HUB} element={<PrivateRoute component={DocumentHub} title={t('RoutesTitle.DocumentHub')} />} />
                        <Route index path={ROUTES.MY_QUOTE} element={<PrivateRoute component={MyQuote} title={t('RoutesTitle.SmartQuote')} permission={PERMISSION.VIEW_SMART_QUOTE} />} />
                        <Route index path={ROUTES.QUOTE_DETAILS} element={<PrivateRoute component={QuoteDetail} title={t('RoutesTitle.MyQuote')} />} />

                        <Route index path={ROUTES.CREATE_QUOTE} element={<PrivateRoute component={CreateQuote} title={t('RoutesTitle.AddNewQuote')} />} />
                        <Route index path={ROUTES.ADD_QUOTE} element={<PrivateRoute component={AddQuote} title={t('RoutesTitle.AddNewQuote')} />} />
                        <Route index path={ROUTES.BOOKING_STEPPER} element={<PrivateRoute component={BookingStepper} title={t('RoutesTitle.BookingStepper')} />} />
                        <Route path={ROUTES.SETTING} element={<PrivateRoute component={AccountSettings} title={t('RoutesTitle.AccountSettings')} permission={true} />} />
                        <Route index path={ROUTES.ORDERS} element={<PrivateRoute component={Orders} title={t('RoutesTitle.Orders')} permission={true} />} />

                        <Route path={ROUTES.ORDER_DETAILS_VIEW} element={<PrivateRoute component={OrderView} title={t('RoutesTitle.OrderDetails')} />} />

                        {/* Track — both list and detail share ONE Mapbox instance via TrackMapLayout */}
                        <Route element={<TrackMapLayout />}>
                            <Route path={ROUTES.TRACK} element={<PrivateRoute component={Track} title={t('RoutesTitle.Track')} permission={true} />} />
                            <Route path={ROUTES.TRACK_ORDER_DETAIL} element={<PrivateRoute component={TrackOrderDetail} title={t('RoutesTitle.Track')} permission={true} />} />
                        </Route>
                        <Route path={ROUTES.INTEGRATION} element={<PrivateRoute component={Integration} title={t('RoutesTitle.Integrations')} />} />
                        <Route path={ROUTES.ADDRESS_BOOK} element={<PrivateRoute component={AddressBook} title={t('RoutesTitle.AddressBook')} />} />
                    </Route>

                    <Route path={'/'}>
                        <Route path={ROUTES.CALLBACK} element={<Callback />} />
                        <Route index path={ROUTES.MICROSOFT_CALLBACK} element={<MicrosoftCallback />} />
                    </Route>

                    {/* Public, JWE-tokenized share-link pages — rendered OUTSIDE AppLayout so
                        the top header and sidebar do not appear. Anonymous viewers; the token
                        in ?t=... is the only authorization. */}
                    <Route path={ROUTES.SHARE_QUOTE_LINK} element={<SharedQuote />} />
                    <Route path={ROUTES.SHARE_ORDER_LINK} element={<ShareOrder />} />
                    <Route path={ROUTES.SHARE_RADAR_ORDER_LINK} element={<ShareRadarOrder />} />
                    <Route path={ROUTES.PUBLIC_TRACKING} element={<PublicTracking />} />

                    <Route path="*" element={<PageNotFound />} />
                </ReactRoutes>
            </AuthCheck>
        </BrowserRouter>
    );
};

export default Routes;
